import { type PlaceholderStateMeta, type RouteRecordRaw } from '@bv/router';
import { useUserStore } from '@bv/stores/user';
import { getSupportWhatsappUrl } from '@bv/utils/common/get-whatsapp-url';
import { generateBreadcrumbs } from '@bv/utils/route/breadcrumbs/generate-breadcrumbs';
import { getConsoleUrl } from '@bv/utils/url/console/get-url';
import { computed, defineAsyncComponent } from 'vue';

const campaignsBreadcrumbs = generateBreadcrumbs({
  label: 'global.aside.campaigns',
  route: { name: 'campaigns' },
});

const placeholderState: PlaceholderStateMeta = computed(() => {
  const userStore = useUserStore();
  if (!userStore.callCenterSettings?.dialer_service.enabled) {
    return {
      onlyWhenInaccessible: true,
      cardTitle: 'global.aside.campaigns',
      icon: 'bvi-announcement',
      title: 'campaigns.upgrade.title',
      subtitle: 'campaigns.upgrade.subtitle',
      action: {
        text: 'campaigns.upgrade.cta',
        href: getConsoleUrl('subscriptions?t=call-center'),
        target: '_blank',
      },
    };
  } else if (!userStore.callCenterSettings?.dialer_service.context) {
    const isOwner = userStore.currentWorkspace?.is_owner;
    return {
      cardTitle: 'global.aside.campaigns',
      icon: 'bvi-announcement',
      title: 'campaigns.pending.title',
      subtitle: `campaigns.pending.subtitle.${isOwner ? 'owner' : 'non_owner'}`,
      action: isOwner
        ? {
            text: 'campaigns.pending.cta',
            href: getSupportWhatsappUrl('أنا أحتاج إلى تفعيل Campaigns'),
            target: '_blank',
          }
        : undefined,
    };
  }
});

const campaignRoutes: RouteRecordRaw = {
  path: '/campaigns',
  name: 'campaigns',
  meta: {
    header: { title: 'global.aside.campaigns', icon: 'bvi-announcement' },
    canAccess: () => !!useUserStore().callCenterSettings?.dialer_service?.enabled,
    placeholderState,
    redirectUnauthorized: { path: '/dashboard' },
    breadcrumbs: campaignsBreadcrumbs(),
  },
  children: [
    {
      path: '',
      name: 'campaigns-list',
      component: defineAsyncComponent(() => import('@/components/Campaign/CampaignList.vue')),
      meta: {
        permissions: 'read-campaigns',
        breadcrumbs: campaignsBreadcrumbs(),
      },
    },
    {
      path: 'add',
      name: 'campaigns-add',
      component: defineAsyncComponent(() => import('@/components/Campaign/CampaignForm.vue')),
      meta: {
        permissions: 'write-campaigns',
        breadcrumbs: campaignsBreadcrumbs(),
      },
    },
    {
      path: 'edit/:id',
      name: 'campaigns-edit',
      component: defineAsyncComponent(() => import('@/components/Campaign/CampaignForm.vue')),
      meta: {
        isEdit: true,
        permissions: 'update-campaigns',
        breadcrumbs: campaignsBreadcrumbs(),
      },
    },
    {
      path: 'report/:id',
      name: 'campaigns-report',
      component: defineAsyncComponent(() => import('@/components/Campaign/reports/CampaignReports.vue')),
      meta: {
        permissions: 'report-campaigns',
        breadcrumbs: campaignsBreadcrumbs({
          label: 'campaigns.list.report_trans',
          route: { name: 'campaigns-report' },
        }),
      },
    },
  ],
};
export default campaignRoutes;
